import {APIRequest} from "../APIRequest.js";
import {KB_API_URL} from "./KBAPI.js";
import {convertS3ObjectsToTree, createFile, createFolder} from "../../Code/CodeEditorUtils";
import jwtDecode from "jwt-decode";


const CACHE_EXPIRATION_TIME = 0.5 * 60 * 1000 - 20000;  // 5 minutes - 20 seconds
const cache = {};

const cacheKey = (filename, namespace, kbId, handleFileWithAPI) => `${filename}-${namespace}-${kbId}-${handleFileWithAPI}`;

const cleanupCache = () => {
    for (const key in cache) {
        if (cache[key] && (Date.now() - cache[key].timestamp >= CACHE_EXPIRATION_TIME)) {
            delete cache[key];
        }
    }
};

export const deleteRawKBFile = async (fileName, namespace) => {
    const token = localStorage.getItem('kbJWT');

    const apiPayload = {
        token,
        namespace,
        action: 'deleteRawKBFile',
        fileName
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);
    return result;
}

export const deleteKBFile = async (fileName, namespace) => {
    const token = localStorage.getItem('kbJWT');

    const apiPayload = {
        token,
        namespace,
        action: 'deleteKBFile',
        fileName,
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);
    return result;
}

export const buildNodePackageWithKBToken = async (namespace, moduleName) => {
    const token = localStorage.getItem('kbJWT');

    const apiPayload = {
        token,
        namespace,
        moduleName,
        action: 'buildNodePackageWithKBToken',
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);

    return result;
}

export const buildWebpackPackageWithKBToken = async (namespace, moduleName) => {
    const token = localStorage.getItem('kbJWT');

    return await APIRequest('post', KB_API_URL, {
        token,
        namespace,
        moduleName,
        action: 'buildWebpackPackageWithKBToken',
    });
}


export const buildNodePackage = async (namespace, kbId, moduleName) => {
    const token = localStorage.getItem("clientJWT");

    const apiPayload = {
        token,
        kbId,
        namespace,
        moduleName,
        action: 'buildNodePackage',
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);

    return result;
}

export const buildWebpackPackage = async (namespace, kbId, moduleName) => {
    const token = localStorage.getItem("clientJWT");

    return await APIRequest('post', KB_API_URL, {
        token,
        kbId,
        namespace,
        moduleName,
        action: 'buildWebpackPackage',
    });
}

export const listFiles = async (namespace) => {
    const token = localStorage.getItem('kbJWT');

    const {kbId} = jwtDecode(token);

    const apiPayload = {
        token,
        namespace,
        action: 'listFiles',
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);

    let modifiedData = [...convertS3ObjectsToTree(result?.data)];

    if (namespace === 'frontend') {
        createFolder(modifiedData, 'Frontend', namespace);
        createFile(modifiedData, 'contentRender.js',  'Frontend', kbId, namespace)
        createFile(modifiedData, 'contentRender.json',  'Frontend', kbId, namespace)
    } else if (namespace === 'functions') {
        createFolder(modifiedData, 'Events', namespace);
        createFile(modifiedData, 'onRequest.js', 'Events', kbId, namespace)
        createFile(modifiedData, 'onRequest.json', 'Events', kbId, namespace)

        createFile(modifiedData, 'onResponse.js',  'Events', kbId, namespace)
        createFile(modifiedData, 'onResponse.json',  'Events', kbId, namespace)

        createFile(modifiedData, 'onAddMessages.js',  'Events', kbId, namespace)
        createFile(modifiedData, 'onAddMessages.json',  'Events', kbId, namespace)

        createFile(modifiedData, 'onPublicAPIRequest.js',  'Events', kbId, namespace)
        createFile(modifiedData, 'onPublicAPIRequest.json',  'Events', kbId, namespace)

        createFile(modifiedData, 'onCronjob.js',  'Events', kbId, namespace)
        createFile(modifiedData, 'onCronjob.json',  'Events', kbId, namespace)
    }

    return modifiedData;
}

export const restoreVersion = async (version, fileName, namespace) => {
    const token = localStorage.getItem('kbJWT');

    const apiPayload = {
        token,
        namespace,
        version,
        action: 'restoreVersion',
        fileName,
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);

    return result;
}

export const deleteVersion = async (version, fileName, namespace) => {
    const token = localStorage.getItem('kbJWT');

    const apiPayload = {
        token,
        namespace,
        version,
        action: 'deleteVersion',
        fileName,
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);

    return result;
}

export const createSecret = async (secretName, secretValue, kbId) => {
    const token = localStorage.getItem("clientJWT");
    return await APIRequest('post', KB_API_URL, {
        token,
        kbId,
        action: 'createSecret',
        secretName,
        secretValue
    });
}

export const createSecretWithKBToken = async (secretName, secretValue) => {
    const token = localStorage.getItem('kbJWT');

    return await APIRequest('post', KB_API_URL, {
        token,
        action: 'createSecretWithKBToken',
        secretName,
        secretValue
    });
}

export const deleteSecret = async (secretName) => {
    const token = localStorage.getItem('kbJWT');

    return await APIRequest('post', KB_API_URL, {
        token,
        action: 'deleteSecret',
        secretName,
    });
}

export const listSecrets = async () => {
    const token = localStorage.getItem('kbJWT');
    return await APIRequest('post', KB_API_URL, { token, action: 'listSecrets'});
}

export const listVersions = async (fileName, namespace) => {
    const token = localStorage.getItem('kbJWT');

    const apiPayload = {
        token,
        namespace,
        action: 'listVersions',
        fileName,
    };

    // Make a request to the API
    const result = await APIRequest('post', KB_API_URL, apiPayload);

    return result;
}

export const copyAppImage = async (appId, kbId) => {
    const token = localStorage.getItem("clientJWT");

    return await APIRequest('post', KB_API_URL, {
        token,
        kbId,
        appId,
        action: 'copyAppImage'
    });
}

export const copyFile = async (namespace, subfolder, fileName, fileContent, kbId) => {
    const token = localStorage.getItem("clientJWT");

    return await APIRequest('post', KB_API_URL, {
        token,
        kbId,
        namespace,
        action: 'copyFile',
        subfolder,
        fileName,
        fileContent
    });
}

export const cloneFiles = async (namespace, subfolder, appId, kbId) => {
    const token = localStorage.getItem("clientJWT");

    return await APIRequest('post', KB_API_URL, {
        token,
        kbId,
        namespace,
        appId,
        action: 'cloneFiles',
        subfolder
    });
}

export const handleFileWithAPI = async (selectedFile, namespace, onUploadProgress, presignedOperation) => {
    const token = localStorage.getItem('kbJWT');

    const {kbId} = jwtDecode(token);

    // @Todo this handles openAI error (to remove it if fixed)
    let fileName = selectedFile?.name?.endsWith('.jpg')
        ? selectedFile.name.replace('.jpg', '.jpeg')
        : selectedFile.name;

    // Generate a unique key for this combination
    const key = cacheKey(fileName, namespace, kbId, presignedOperation);

    let result;

    // Check if the presignedURL is cached and still valid
    if (cache[key] && (Date.now() - cache[key].timestamp < CACHE_EXPIRATION_TIME)) {
        result = cache[key].data;
    } else {
        // Construct API request payload based on action
        const apiPayload = {
            token,
            namespace,
            presignedOperation,
            action: 'createPresignedURL',
            fileName: fileName,
            fileType: selectedFile.type,
        };

        // Make a request to the API
        result = await APIRequest('post', KB_API_URL, apiPayload);

        // Cache the response along with the current timestamp
        cache[key] = {
            data: result,
            timestamp: Date.now()
        };

        cleanupCache();
    }

    if (!result?.data) return;

    // Handle request based on action
    if (presignedOperation === 'putObject') {
        let hasProgress = false;
        const uploadResult = await APIRequest('put', result.data, selectedFile, {
            headers: {"Content-Type": selectedFile.type},
            onUploadProgress: (e) => {
                hasProgress = true;
                if (typeof onUploadProgress === 'function') onUploadProgress(Math.round((e.loaded * 100) / e.total));
            }
        });

        if (onUploadProgress && !hasProgress) onUploadProgress(100);

        return uploadResult;
    } else if (presignedOperation === 'getObject') {
        let fetchResult = await APIRequest('get', result.data);

        // convert numeric file content to string
        // @Todo make sure this function is only used for code editing
        if (fetchResult?.data && typeof fetchResult.data === 'number') {
            fetchResult.data = fetchResult.data.toString();
        }

        return fetchResult;
    }
};

export const uploadFileAPI = (selectedFile, namespace, onUploadProgress) => {
    return handleFileWithAPI(selectedFile, namespace, onUploadProgress, 'putObject');
};

export const createPresignedURL = async (namespace, presignedOperation, fileName, fileType) => {
    const token = localStorage.getItem('kbJWT');

    const apiPayload = {
        token,
        namespace,
        presignedOperation,
        action: 'createPresignedURL',
        fileName: fileName,
        fileType: fileType,
    };

    return await APIRequest('post', KB_API_URL, apiPayload);
};

export const fetchFileAPI = (selectedFile, namespace) => {
    return handleFileWithAPI(selectedFile, namespace, null, 'getObject');
};

export const renameFile = async (oldFileName, newFileName, namespace = 'files') => {
    // Step 1: Download the file content
    const downloadUrl = `https://web.file.vpc1.us/${oldFileName}`;
    const downloadResponse = await fetch(downloadUrl);
    if (!downloadResponse.ok) throw new Error('Failed to download file');
    const fileBlob = await downloadResponse.blob();

    // Step 2: Upload with new name
    const presignedResponse = await createPresignedURL(
        namespace,
        'putObject',
        newFileName,
        fileBlob.type || 'application/octet-stream'
    );

    // Extract URL from axios response
    const presignedUrl = presignedResponse.data;
    if (!presignedUrl) throw new Error('Failed to get presigned URL');

    // Upload the file with new name
    const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: fileBlob,
        headers: {
            'Content-Type': fileBlob.type || 'application/octet-stream'
        }
    });

    if (!uploadResponse.ok) throw new Error('Failed to upload file');

    // Step 3: Delete the old file
    const oldFileNameOnly = oldFileName.split('/').pop();
    await deleteRawKBFile(oldFileNameOnly, namespace);

    return { success: true };
};